import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { EVENTS, track } from "@/lib/analytics";

// The answer ids are the contract with the backend (AiPlateResult maps them onto
// the English labels /api/auto-plate expects, and nutrition.py matches on those).
// Only what the guest reads is translated — an id or a label sent to the server
// must never move with the interface language.
const QUESTIONS = [
  {
    key: "hunger",
    titleKey: "quiz.hungerTitle",
    answers: [
      { id: "light", emoji: "🟡", key: "quiz.hungerLight" },
      { id: "medium", emoji: "🟠", key: "quiz.hungerMedium" },
      { id: "high", emoji: "🔴", key: "quiz.hungerHigh" },
    ],
  },
  {
    key: "protein",
    titleKey: "quiz.proteinTitle",
    answers: [
      { id: "beef", emoji: "🥩", key: "quiz.proteinBeef" },
      { id: "chicken", emoji: "🍗", key: "quiz.proteinChicken" },
      { id: "fish", emoji: "🐟", key: "quiz.proteinFish" },
      { id: "eggs", emoji: "🥚", key: "quiz.proteinEggs" },
    ],
  },
  {
    key: "goal",
    titleKey: "quiz.goalTitle",
    answers: [
      { id: "protein", emoji: "💪", key: "quiz.goalProtein" },
      { id: "energy", emoji: "⚡", key: "quiz.goalEnergy" },
      { id: "fatloss", emoji: "🔥", key: "quiz.goalFatloss" },
      { id: "balanced", emoji: "⚖️", key: "quiz.goalBalanced" },
    ],
  },
  {
    key: "restriction",
    titleKey: "quiz.restrictionTitle",
    answers: [
      { id: "dairy", emoji: "🥛", key: "quiz.restrictionDairy" },
      { id: "gluten", emoji: "🌾", key: "quiz.restrictionGluten" },
      { id: "noraw", emoji: "🍳", key: "quiz.restrictionNoraw" },
      { id: "none", emoji: "✅", key: "quiz.restrictionNone" },
    ],
  },
];

/** Every answer id mapped to the catalog key that names it, for the recap line. */
const ANSWER_LABELS = Object.fromEntries(
  QUESTIONS.flatMap((question) => question.answers.map((a) => [`${question.key}:${a.id}`, a.key]))
);

/**
 * What a guest's stated allergies mean in the quiz's own vocabulary.
 *
 * The server maps the same words onto the same tags when it builds the prompt
 * (ALLERGEN_SYNONYMS in auto_plate.py), so a guest whose profile says "milk" is
 * already never served dairy. Preselecting the matching answer stops the quiz
 * from quietly contradicting the profile — it is preselected, never skipped: a
 * restriction is answered for this meal, by the person eating it.
 */
const PROFILE_RESTRICTIONS = [
  ["dairy", ["dairy", "milk", "lactose", "cheese", "butter", "cream", "yoghurt", "yogurt", "whey", "casein"]],
  ["gluten", ["gluten", "wheat", "bread", "flour", "pasta", "barley", "rye"]],
];

const restrictionFromProfile = (user) => {
  const words = [...(user?.allergies || []), ...(user?.exclude_ingredients || [])].map((w) =>
    String(w).trim().toLowerCase()
  );
  if (words.length === 0) return null;
  const hit = PROFILE_RESTRICTIONS.find(([, aliases]) => words.some((w) => aliases.includes(w)));
  return hit ? hit[0] : null;
};

const Quiz = ({ onBack, onComplete, lastAnswers, initialAnswers, initialStep = 0 }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  // Coming back from the free-text step resumes at the last question with the
  // answers still picked — landing on question one read as the quiz forgetting.
  const [step, setStep] = useState(initialStep);
  const [answers, setAnswers] = useState(initialAnswers || {});
  const [skippedRecap, setSkippedRecap] = useState(false);

  const q = QUESTIONS[step];
  const profileRestriction = restrictionFromProfile(user);

  // Answered before, on this device, at this restaurant. Most guests never sign
  // in, so the device is what remembers — and lunch tomorrow is usually lunch
  // today, which is four taps a returning guest should not have to repeat.
  const recap =
    !skippedRecap && step === 0 && lastAnswers
      ? QUESTIONS.map((question) => lastAnswers[question.key])
      : null;
  const recapComplete = recap && recap.every(Boolean);

  const pick = (ansId) => {
    const next = { ...answers, [q.key]: ansId };
    setAnswers(next);
    // The answer id, not the label the guest read: the id is the contract with
    // the backend and does not move with the interface language, so a report is
    // not three reports with the same shape.
    track(EVENTS.QUIZ_ANSWERED, {
      step: q.key,
      position: step + 1,
      answer: ansId,
      source: "fresh",
      // Whether the quiz arrived pre-answered from the account's own profile —
      // a restriction nobody picked here still shapes the plate.
      from_profile: q.key === "restriction" && ansId === profileRestriction,
    });
    setTimeout(() => {
      if (step < QUESTIONS.length - 1) setStep(step + 1);
      else onComplete(next);
    }, 180);
  };

  const back = () => {
    if (step === 0) onBack();
    else setStep(step - 1);
  };

  const TOTAL_STEPS = 5; // 4 quiz questions + 1 free-input step (Q5Input)
  const progress = ((step + 1) / TOTAL_STEPS) * 100;

  return (
    <div className="px-5 pt-6 pb-10 min-h-screen flex flex-col">
      <div className="flex items-center gap-3 mb-6">
        <button
          data-testid="quiz-back"
          onClick={back}
          className="w-10 h-10 rounded-full bg-white border border-neutral-200 flex items-center justify-center shrink-0"
          aria-label={t("common.back")}
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-neutral-500">
            {t("quiz.overline", { step: step + 1, total: TOTAL_STEPS })}
          </div>
          <div className="h-1.5 bg-neutral-200 rounded-full mt-1.5 overflow-hidden">
            <div
              data-testid="quiz-progress"
              className="h-full bg-[#E34A27] transition-all duration-300 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>

      {recapComplete && (
        <div
          data-testid="quiz-same-as-last"
          className="mb-6 rounded-3xl border-2 border-[#E34A27]/30 bg-[#E34A27]/[0.06] p-4"
        >
          <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-[#E34A27]">
            {t("quiz.lastTime")}
          </div>
          <div className="text-sm font-bold text-neutral-900 mt-1.5 leading-snug">
            {QUESTIONS.map((question) => t(ANSWER_LABELS[`${question.key}:${lastAnswers[question.key]}`]))
              .filter(Boolean)
              .join(" · ")}
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <button
              data-testid="quiz-same-continue"
              onClick={() => {
                // Four questions answered in one tap. Reported as one event per
                // answer so "which goal" reads the same whichever way it arrived,
                // with `source` telling the two apart.
                QUESTIONS.forEach((question, index) =>
                  track(EVENTS.QUIZ_ANSWERED, {
                    step: question.key,
                    position: index + 1,
                    answer: lastAnswers[question.key],
                    source: "same_as_last",
                  }),
                );
                onComplete(lastAnswers);
              }}
              className="h-11 rounded-2xl bg-[#E34A27] text-white font-bold text-sm active:scale-[0.98] transition-transform"
            >
              {t("quiz.lastTimeUse")}
            </button>
            <button
              data-testid="quiz-same-change"
              onClick={() => setSkippedRecap(true)}
              className="h-11 rounded-2xl bg-white border border-neutral-200 text-neutral-800 font-bold text-sm active:scale-[0.98] transition-transform"
            >
              {t("quiz.lastTimeChange")}
            </button>
          </div>
        </div>
      )}

      <h1 className="text-[32px] leading-[1.1] font-black tracking-tight text-neutral-900 mb-6">
        {t(q.titleKey)}
      </h1>

      <div className="space-y-3 flex-1">
        {q.answers.map((a) => {
          const suggested =
            q.key === "restriction" && !answers[q.key] && profileRestriction === a.id;
          const active = answers[q.key] === a.id;
          return (
            <button
              key={a.id}
              data-testid={`quiz-${q.key}-${a.id}`}
              onClick={() => pick(a.id)}
              className={`w-full text-left rounded-3xl p-5 border-2 transition-all active:scale-[0.99] flex items-start gap-4 ${
                active || suggested
                  ? "border-[#E34A27] bg-[#E34A27]/5"
                  : "border-neutral-200 bg-white"
              }`}
            >
              <div className="text-3xl leading-none mt-0.5 shrink-0">{a.emoji}</div>
              <div className="flex-1 min-w-0">
                <div className="text-lg font-black text-neutral-900 leading-tight flex items-center gap-2 flex-wrap">
                  {t(a.key)}
                  {suggested && (
                    <span
                      data-testid="quiz-from-profile"
                      className="text-[9px] font-black uppercase tracking-wider text-[#E34A27] bg-[#E34A27]/10 rounded-full px-2 py-0.5"
                    >
                      {t("quiz.fromProfile")}
                    </span>
                  )}
                </div>
                <div className="text-sm text-neutral-500 mt-0.5">{t(`${a.key}Desc`)}</div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default Quiz;
